import { YearsService } from '../../../../@core/utils/years.service';
import { Component, Input, OnChanges, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { NbDialogService, NbCardModule, NbAlertModule, NbButtonModule } from '@nebular/theme';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import { combineLatest, Subscription } from 'rxjs';
import * as R from 'ramda';
import { KPI } from '../../../../@core/api';
import {
  ChartFiltersService,
  ChartsService,
  ConfigService,
  PerimeterPathwayService,
  PerimetersService,
  UtilsService,
} from '../../../../@core/utils';
import { LayoutService } from '../../../../@core/utils';
import { switchMap } from 'rxjs/operators';
import { ConsumptionEmissionFilterComponent } from '../../consumption-emission-filter/consumption-emission-filter.component';
import { HighchartsChartModule } from 'highcharts-angular';
import { EnergySettingsFabComponent } from '../../energy-settings-fab/energy-settings-fab.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgClass } from '@angular/common';
import { EmissionFactorService } from '../../../../@core/utils/emission-factor.service';
import { BaseTrajectoryChartComponent } from '../base-trajectory-chart/base-trajectory-chart.component';

@Component({
  selector: 'app-initiative-trajectory-chart',
  styleUrls: ['./initiative-trajectory-chart.component.scss'],
  templateUrl: './initiative-trajectory-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NbCardModule,
    NgClass,
    
    FontAwesomeModule,
    EnergySettingsFabComponent,
    HighchartsChartModule,
    ConsumptionEmissionFilterComponent,
    NbAlertModule,
    NbButtonModule,
    TranslateModule
  ],
})
export class InitiativeTrajectoryChartComponent
  extends BaseTrajectoryChartComponent
  implements OnInit, OnDestroy, OnChanges {

  @Input() public initiative: KPI.MappedInitiative;

  private subscriptions: Subscription[] = [];

  private isEmission: boolean;
  private pathwayStartYear = new Date().getFullYear() - 3;
  private yearsRange: number[] = R.range(this.pathwayStartYear, 2056);
  public displaySettingsFab: boolean;

  constructor(
    protected dialogService: NbDialogService,
    protected layoutService: LayoutService,
    protected router: Router,
    protected pathwayService: PerimeterPathwayService,
    private translate: TranslateService,
    private chartsService: ChartsService,
    private chartFiltersService: ChartFiltersService,
    private yearsService: YearsService,
    private utilsService: UtilsService,
    private cdRef: ChangeDetectorRef,
    private perimetersService: PerimetersService,
    private emissionFactorService: EmissionFactorService,
    private config: ConfigService
  ) {
    super(dialogService, layoutService, router, pathwayService);
  }

  public ngOnChanges(): void {
    super.ngOnInit();
    this.subscriptions.push(
      this.config.customerConfig$.subscribe((conf) => this.displaySettingsFab = conf.energy_carbon_settings_icon_display)
    )
    this.update();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  protected update(): void {
    if (this.initiative) {
      const consumptionTypeObs = this.chartFiltersService.filter$('feasibilityConsumptionOrEmission').pipe(
        switchMap((emissionOrConsumption) => emissionOrConsumption === KPI.FeasibilityConsumptionOrEmissionFilter.EMISSION
          ? this.chartFiltersService.filter$('carbonConsumptionType', false)
          : this.chartFiltersService.filter$('energyConsumptionType', false)
        ),
      );
      this.subscriptions.push(
        combineLatest([
          consumptionTypeObs.pipe(
            switchMap((consumptionType: KPI.ConsumptionType) =>
              this.chartsService.getInitiativeTrajectoryData(this.initiative.id, consumptionType)
            ),
          ),
          this.yearsService.latestYears$,
          this.chartFiltersService.filter$('feasibilityConsumptionOrEmission'),
          combineLatest([
            this.emissionFactorService.getSelectedProvider$('carbon'),
            this.emissionFactorService.getSelectedProvider$('energy'),
          ]),
          this.perimetersService.perimeters$.pipe(
            switchMap((perimeters) =>
              this.pathwayService.requestPathway$(perimeters, this.yearsRange),
            ),
          ),
          this.pathwayService.getSelectedPathway(),
        ]).subscribe(([chartData, [startYear, endYear], consumptionOrEmission, [carbonProvider, energyProvider], energyPathway, selectedToUPathway]) => {

          this.loading = true;
          this.isEmission = consumptionOrEmission === KPI.FeasibilityConsumptionOrEmissionFilter.EMISSION;
          const initiativeYear = new Date(this.initiative.savings_start_at).getFullYear();
          const years = R.range(
            Math.min(initiativeYear || startYear, startYear),
            Math.max(initiativeYear || endYear, endYear) + 1
          );
          const baseSavingsSerie = {
            type: 'column',
            yAxis: 'y-axis-2',
            zIndex: 1,
            opacity: 0.9,
          }

          const energyConsumptionTrajectory = chartData.energy_consumption_trajectory;
          const CO2EmissionTrajectory = chartData.ghg_emission_trajectory;
          const initiativeSavings = chartData.initiative_savings;
          const initiativeCo2Savings = chartData.initiative_ghg_savings;
          const buildingSavings = chartData.initiatives_building_savings;
          const buildingCo2Savings = chartData.initiatives_building_ghg_savings;

          const objectiveSerieTemplate = {
            dashStyle: 'dash',
            lineWidth: 1.5,
            marker: {
              enabled: false,
            },
            dataLabels: {
              enabled: false
            },
            objective: true,
            column: false,
            trajectory: false,
            type: 'line',
            yAxis: 'y-axis-1',
            zIndex: 3,
          };

          const providerId = this.isEmission ? carbonProvider.id : energyProvider.id

          const objectiveData2030 =  (providerId === null || providerId === 0)
          ? []
          : providerId !== -1
            ? this.isEmission
              ? this.getObjectiveDataByToU(years.length, selectedToUPathway.emission['2030'])
              : this.getObjectiveDataByToU(years.length, selectedToUPathway.consumption['2030'])
            : this.isEmission
              ? this.getObjectiveDataBySite(years.length, energyPathway.site_objectives, 2030, 'ghg')
              : this.getObjectiveDataBySite(years.length, energyPathway.site_objectives, 2030, 'energy')

          const objectiveData2040 =   (providerId === null || providerId === 0)
          ? []
          : providerId !== -1
            ? this.isEmission
              ? this.getObjectiveDataByToU(years.length, selectedToUPathway.emission['2040'])
              : this.getObjectiveDataByToU(years.length, selectedToUPathway.consumption['2040'])
            : this.isEmission
              ? this.getObjectiveDataBySite(years.length, energyPathway.site_objectives, 2040, 'ghg')
              : this.getObjectiveDataBySite(years.length, energyPathway.site_objectives, 2040, 'energy')

          const objectiveData2050 =  (providerId === null || providerId === 0)
            ? []
            : providerId !== -1
              ? this.isEmission
                ? this.getObjectiveDataByToU(years.length, selectedToUPathway.emission['2050'])
                : this.getObjectiveDataByToU(years.length, selectedToUPathway.consumption['2050'])
              : this.isEmission
                ? this.getObjectiveDataBySite(years.length, energyPathway.site_objectives, 2050, 'ghg')
                : this.getObjectiveDataBySite(years.length, energyPathway.site_objectives, 2050, 'energy')

          const series = [
            {
              name: this.translate.instant('Action impact'),
              data: years.map((year) =>
                (this.isEmission
                  ? initiativeCo2Savings[year] > 0
                    ? initiativeCo2Savings[year]
                    : null
                  : initiativeSavings[year] > 0
                    ? initiativeSavings[year]
                    : null
                ) || null),
              ...baseSavingsSerie,
              initiativeSavings: true,
              showInLegend: true,
            },
            {
              name: !this.isEmission ?
                this.translate.instant('kWh consumption linked to action')
                :
                this.translate.instant('CO2 consumption linked to action'),
              data: years.map((year) =>
                (this.isEmission
                  ? initiativeCo2Savings[year] < 0
                    ? Math.abs(initiativeCo2Savings[year])
                    : null
                  : initiativeSavings[year] < 0
                    ? Math.abs(initiativeSavings[year])
                    : null
                ) || null),
              ...baseSavingsSerie,
              initiativeSavings: true,
              showInLegend: false,
            },
            {
              name: this.translate.instant('Other planned savings'),
              data: years.map((year) => buildingSavings[year]
                ? (this.isEmission ?
                  (buildingCo2Savings[year] - (initiativeCo2Savings[year] || 0)) : (buildingSavings[year] - (initiativeSavings[year] || 0))
                )
                : null
              ),
              ...baseSavingsSerie,
              allSavings: true,
            },
            {
              name: !this.isEmission ?
                this.translate.instant('Planned kwh building ratio')
                :
                this.translate.instant('Planned co2 building ratio'),
              data: years.map((year) => {
                const trajectory = (this.isEmission ? CO2EmissionTrajectory : energyConsumptionTrajectory).find(t => t.year === year);
                return (this.isEmission ?
                    ((trajectory as KPI.MinimalCO2Trajectory)?.ghg_emission / trajectory?.area)
                    : Math.round((trajectory as KPI.MinimalEnergyTrajectory)?.energy_consumption / trajectory?.area)
                  )|| null;
              }),
              lineWidth: 1,
              marker: {
                symbol: 'square',
                radius: 5
              },
              yAxis: 'y-axis-1',
              zIndex: 2,
              showInLegend: true,
            },
            // OBJECTIVE 2030
            !this.isEmission && {
              ...objectiveSerieTemplate,
              name: this.translate.instant('Objective')
                + ' 2030',
              tooltipName: this.translate.instant('Objective') + ' 2030',
              data: objectiveData2030,
              lineWidth: 2,
              showInLegend: !!objectiveData2030[0],
              visible: !!objectiveData2030[0],
            },
            // OBJECTIVE 2040
            !this.isEmission && {
              ...objectiveSerieTemplate,
              name: this.translate.instant('Objective') + ' 2040',
              tooltipName: this.translate.instant('Objective') + ' 2040',
              data: objectiveData2040,
              lineWidth: 2,
              showInLegend: !!objectiveData2040[0],
              visible: !!objectiveData2040[0],
            },
            // OBJECTIVE 2050
            !this.isEmission && {
              ...objectiveSerieTemplate,
              name: this.translate.instant('Objective') + ' 2050',
              tooltipName: this.translate.instant('Objective') + ' 2050',
              data: objectiveData2050,
              lineWidth: 2,
              showInLegend: !!objectiveData2050[0],
              visible: !!objectiveData2050[0],
            },
          ].filter(Boolean);

          const categoryNames = years;

          // Update chart options
          this.options = {
            ...this.getOptions(),
            // @ts-ignore
            xAxis: {
              ...this.getOptions().xAxis,
              // @ts-ignore
              categories: categoryNames,
              tickWidth: 0,
              labels: {
                style: {
                  fontSize: '13px',
                },
              },
            },
            // @ts-ignore
            series: series as Highcharts.SeriesColumnOptions[],
          };
          this.loading = false;
          setTimeout(() => {
            this.reflow();
            this.cdRef.detectChanges();
          }, 500);
        }),
      );
    }
  }

  protected getOptions(): Highcharts.Options {
    const translate = this.translate;
    const utilsService = this.utilsService;
    const isEmission = this.isEmission;
    const formateData = this.formateData;

    return {
      ...super.getOptions(),
      colors: [
        '#6CB8C0',
        '#FF0000',
        '#D0CECE',
        '#4472C4',
        '#00b0f0',
        '#ffc000',
        '#ff9b9b',
      ],
      title: {
        text: '',
      },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '0.9375rem',
          fontWeight: 'normal',
        },
        itemHiddenStyle: super.getOptions().legend.itemHiddenStyle,
        itemMarginBottom: 15,
        itemMarginTop: 0,
        symbolHeight: 11,
        symbolRadius: 0,
      },
      tooltip: {
        enabled: true,
        split: true,
        shared: true,
        useHTML: true,
        formatter: function () {
          let tooltipText: string[] = [''].concat(
            this.points
              ? this.points.map((point) => {
                let text: string;
                let diffPercent: number;
                let formatPercent: string;

                if (
                  !point.series.userOptions["objective"]
                  && !point.series.userOptions["initiativeSavings"]
                  && !point.series.userOptions["allSavings"]
                ) {
                  text = '<b> '
                    + point.series.name
                    + ': </b>'
                    + (isEmission ? utilsService.formateNumber(point.y.toFixed(1)) : utilsService.formateNumber(Math.round(point.y)))
                    + ' '
                    + (isEmission ? translate.instant(_('pages.energy.kg_CO2')) : translate.instant(_('pages.energy.kwh_ef_m2_year')));
                  if (diffPercent > 0) {
                    text += ' : '
                      + formatPercent
                      + translate.instant(_('pages.energy.vsObjective'));
                  }
                  else if (diffPercent === 0) {
                    text += ' : <span style="color:green">'
                      + translate.instant(_('pages.decision.analysis.cards.objective-reached'))
                      + '</span>';
                  }
                  else if (diffPercent < 0) {
                    text += ' : <span style="color:green">'
                      + translate.instant(_('pages.decision.analysis.cards.objective-exceeded'))
                      + '</span>';
                  }
                } else if (
                  point.series.userOptions["initiativeSavings"]
                  || point.series.userOptions["allSavings"]
                ) {
                  text = `<b>${point.series.name}: </b>` + formateData(isEmission, point.y, translate, utilsService, true)
                } else {
                  return '';
                }
                return text;
              })
              : []
          );
          return tooltipText;
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 0,
          cursor: 'pointer',
        },
        spline: {
          dataLabels: {
            enabled: false,
          },
        },
      },
      yAxis: [{
        id: 'y-axis-1',
        gridLineWidth: 1,
        minorGridLineWidth: 0,
        labels: {
          enabled: false,
        },
        title: {
          text: undefined,
        },
      },
      {
        id: 'y-axis-2',
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        labels: {
          enabled: false,
        },
        title: {
          text: undefined,
        },
        opposite: true,
        stackLabels: {
          style: {
            fontSize: '11.5px',
            fontWeight: 'normal',
          },
          enabled: true,
          formatter: function () {
            if (this.total) {
              return formateData(isEmission, this.total, translate, utilsService, false);
            }
          },
        },
      }],
      xAxis: {
        crosshair: true,
        labels: {
          style: {
            fontSize: '12px',
            color: '#091902',
          },
        },
      },
      series: [],
    };
  }

  protected getChartOptions(): Highcharts.ChartOptions {
    return {
      ...super.getChartOptions(),
      type: 'spline',
      alignTicks: false,
    };
  }

  private formateData(
    isEmission: boolean,
    data: number,
    translate: TranslateService,
    utilsService: UtilsService,
    ratio: boolean,
  ): any {
    return (
      isEmission
        ? utilsService.formateNumber(Math.round(data)) + ' ' + translate.instant(_('pages.energy.kg_CO2'))
        : utilsService.round2dec(data / 1000, 1) + ' ' + (ratio ? translate.instant(_('pages.energy.MWh_sub_ef_year')) : translate.instant(_('MWh')))
    )
  }
}
